import React, { useState } from "react";
import { FaChevronDown, FaChevronUp, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { GroqDrawerTrigger } from "../components/GroqDrawerTrigger";

// Hard-coded how-to guides template
const GUIDES = [
  {
    id: 1,
    title: "How to Set Up a Tent",
    category: "Camping",
    difficulty: "Beginner",
    readTime: "5 min",
    overview: "Learn the basics of setting up your tent safely and securely.",
    steps: [
      {
        number: 1,
        title: "Find a Suitable Location",
        description: "Choose flat, dry ground away from hazards. Avoid areas with sharp rocks or dead trees.",
        tips: ["Look for natural windbreaks", "Avoid low-lying areas where water collects", "Stay away from animal trails"]
      },
      {
        number: 2,
        title: "Prepare the Ground",
        description: "Clear debris and lay down a ground tarp or footprint to protect the tent floor.",
        tips: ["Remove stones and sticks", "Consider a tarp underneath", "This prevents moisture and damage"]
      },
      {
        number: 3,
        title: "Assemble the Tent Frame",
        description: "Connect tent poles according to manufacturer instructions. Insert them through sleeves or clips.",
        tips: ["Match color-coded sections", "Ensure poles are fully seated", "Poles should form a gentle arc"]
      },
      {
        number: 4,
        title: "Attach the Tent Body",
        description: "Connect the tent fabric to the frame using clips or clips. Start at one end and work your way around.",
        tips: ["Keep tension even", "Avoid twisting the fabric", "Ensure all clips are secure"]
      },
      {
        number: 5,
        title: "Set Up the Rainfly",
        description: "Attach the rainfly to cover the tent, leaving ventilation gaps at the bottom.",
        tips: ["Rainfly should be taut but not overstretched", "Leave 2-3 inches clearance from tent fabric", "This prevents condensation"]
      },
      {
        number: 6,
        title: "Stake and Secure",
        description: "Drive stakes at 45-degree angles, pulling tent fabric taut. Adjust guy lines for stability.",
        tips: ["Stakes should go deep enough to hold in wind", "Angle away from tent", "Test by gently pulling on tent"]
      }
    ],
    warnings: [
      "Never set up on slopes that could flood",
      "Check weather forecast before camping",
      "Ensure proper ventilation to prevent condensation"
    ],
    tools: ["Tent", "Ground tarp", "Stakes", "Mallet", "Guy lines"]
  },
  {
    id: 2,
    title: "How to Purify Water",
    category: "Hiking",
    difficulty: "Beginner",
    readTime: "8 min",
    overview: "Methods to ensure safe drinking water while outdoors.",
    steps: [
      {
        number: 1,
        title: "Boiling",
        description: "Bring water to a rolling boil for at least 1 minute (3 minutes above 6,500 ft elevation).",
        tips: ["Most reliable method", "Kills 99.9% of pathogens", "Let cool before drinking"]
      },
      {
        number: 2,
        title: "Water Filters",
        description: "Use portable filters (0.1 micron) to remove bacteria, protozoa, and some viruses.",
        tips: ["Follow manufacturer instructions", "Replace cartridges as recommended", "Pre-filter cloudy water"]
      },
      {
        number: 3,
        title: "Chemical Treatment",
        description: "Use iodine tablets or drops. Add to water and wait 30 minutes (longer in cold water).",
        tips: ["Effective against most pathogens", "Can affect taste", "Not suitable for pregnant women"]
      },
      {
        number: 4,
        title: "UV Purification",
        description: "Use battery-powered UV pens to kill microorganisms. Works instantly.",
        tips: ["Fast and lightweight", "Doesn't work on cloudy water", "Requires batteries"]
      }
    ],
    warnings: [
      "Never drink untreated water from natural sources",
      "Giardia and cryptosporidium are common in backcountry water",
      "Combine methods for best results"
    ],
    tools: ["Water container", "Filter/purifier", "Boiling pot", "Lighter"]
  },
  {
    id: 3,
    title: "How to Use a Compass",
    category: "Navigation",
    difficulty: "Beginner",
    readTime: "10 min",
    overview: "Master basic compass navigation for backcountry travel.",
    steps: [
      {
        number: 1,
        title: "Understand Your Compass",
        description: "Identify the baseplate, magnetic needle, orienting lines, and direction arrow.",
        tips: ["Magnetic needle always points north", "Red end is north", "Never store near metal objects"]
      },
      {
        number: 2,
        title: "Set Declination",
        description: "Adjust your compass for magnetic declination in your area (varies by location).",
        tips: ["Check local declination maps", "Some compasses have adjustment screws", "Affects accuracy significantly"]
      },
      {
        number: 3,
        title: "Take a Bearing",
        description: "Place compass on map with direction-of-travel arrow pointing toward destination.",
        tips: ["Align orienting lines with map grid", "Rotate bezel until orienting lines align with map meridians", "Read bearing at index line"]
      },
      {
        number: 4,
        title: "Follow Your Bearing",
        description: "Hold compass level and turn your body until the magnetic needle aligns with orienting lines.",
        tips: ["Direction arrow now points the way", "Choose a landmark to aim for", "Adjust as terrain dictates"]
      }
    ],
    warnings: [
      "Compass alone is not sufficient for navigation",
      "Always carry a map of your area",
      "Practice navigation before backcountry trips"
    ],
    tools: ["Compass", "Topographic map", "Pencil"]
  },
  {
    id: 4,
    title: "How to Build a Campfire",
    category: "Camping",
    difficulty: "Beginner",
    readTime: "6 min",
    overview: "Learn to build and maintain a safe, efficient campfire.",
    steps: [
      {
        number: 1,
        title: "Gather Tinder",
        description: "Collect dry, thin, easily flammable material: dry grass, bark, paper, or commercial fire starters.",
        tips: ["Must be completely dry", "Gather more than you think you need", "Test for dryness by snapping"]
      },
      {
        number: 2,
        title: "Gather Kindling",
        description: "Collect finger-thin sticks and twigs. These catch fire easily and burn quickly.",
        tips: ["Look for dead branches on ground", "Avoid green/fresh wood", "Gather 2-3 times what you think you need"]
      },
      {
        number: 3,
        title: "Gather Fuel Wood",
        description: "Collect arm-thick to wrist-thick branches. These sustain your fire for extended periods.",
        tips: ["Dead wood burns better than green", "Split thick pieces for faster burning", "Have plenty ready before starting"]
      },
      {
        number: 4,
        title: "Create Fire Ring",
        description: "Clear a 10-foot diameter area and arrange rocks in a circle to contain flames.",
        tips: ["Ensure stones won't explode (avoid stream rocks)", "Make sure fire ring is downwind from shelter", "Check local fire restrictions"]
      },
      {
        number: 5,
        title: "Light the Fire",
        description: "Create a teepee with kindling over tinder. Light tinder and gradually add larger wood.",
        tips: ["Provide adequate oxygen between pieces", "Light from multiple sides if needed", "Feed fire gradually"]
      },
      {
        number: 6,
        title: "Extinguish Properly",
        description: "Drown with water, stir ashes, drown again. Fire should be cold to touch.",
        tips: ["Never leave fire unattended", "Fully extinguish before sleeping or leaving", "\"Cold to touch\" is the standard"]
      }
    ],
    warnings: [
      "Always check fire restrictions in your area",
      "Never leave a fire unattended",
      "Be aware of wind conditions",
      "Keep water nearby"
    ],
    tools: ["Lighter/matches", "Tinder", "Kindling", "Fuel wood", "Water"]
  }
];

const HowToPage = () => {
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState({});

  // State and ref for GroqDrawerTrigger (YOGi)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const groqDrawerOpenRef = React.useRef(null);
  const groqSetQueryRef = React.useRef(null);

  const openGroqDrawer = (queryText) => {
    if (groqSetQueryRef.current && typeof groqSetQueryRef.current === 'function' && queryText) {
      groqSetQueryRef.current(queryText);
    }
    if (groqDrawerOpenRef.current) {
      groqDrawerOpenRef.current();
    } else {
      setDrawerOpen(true);
    }
  };

  const toggleStep = (stepNumber) => {
    setExpandedSteps((prev) => ({
      ...prev,
      [stepNumber]: !prev[stepNumber]
    }));
  };

  const currentGuide = selectedGuide 
    ? GUIDES.find((g) => g.id === selectedGuide)
    : null;

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case "Beginner": return "#4caf50";
      case "Intermediate": return "#ff9800";
      case "Advanced": return "#f44336";
      default: return "#999";
    }
  };

  if (currentGuide) {
    return (
      <>
        <div style={{ width: '100%', height: '100%', overflow: 'auto', backgroundColor: '#1a1a1a', color: '#fff', padding: '16px', display: 'flex', flexDirection: 'column' }}>
          {/* Back Button */}
          <button
            onClick={() => setSelectedGuide(null)}
            style={{
              padding: '8px 16px',
              marginBottom: '16px',
              backgroundColor: '#2a2a2a',
              color: '#64b5f6',
              border: '1px solid #444',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
          >
            ← Back to Guides
          </button>



        {/* Guide Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
            {currentGuide.title}
          </h1>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <span style={{ padding: '4px 12px', backgroundColor: '#2a2a2a', borderRadius: '4px', fontSize: '12px', color: '#999' }}>
              {currentGuide.category}
            </span>
            <span style={{ padding: '4px 12px', backgroundColor: '#2a2a2a', borderRadius: '4px', fontSize: '12px', color: getDifficultyColor(currentGuide.difficulty) }}>
              {currentGuide.difficulty}
            </span>
            <span style={{ padding: '4px 12px', backgroundColor: '#2a2a2a', borderRadius: '4px', fontSize: '12px', color: '#999' }}>
              ⏱ {currentGuide.readTime}
            </span>
          </div>
          <p style={{ fontSize: '16px', color: '#ccc', marginBottom: '12px' }}>
            {currentGuide.overview}
          </p>
        </div>

        {/* Tools Needed */}
        <div style={{ marginBottom: '24px', padding: '12px', backgroundColor: '#2a2a2a', borderRadius: '4px', border: '1px solid #444' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#64b5f6' }}>
            Tools/Items Needed:
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {currentGuide.tools.map((tool, idx) => (
              <li key={idx} style={{ padding: '4px 0', color: '#ccc', fontSize: '14px' }}>
                • {tool}
              </li>
            ))}
          </ul>
        </div>

        {/* Steps */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
            Steps
          </h2>
          {currentGuide.steps.map((step) => (
            <div
              key={step.number}
              style={{
                marginBottom: '12px',
                backgroundColor: '#2a2a2a',
                borderRadius: '4px',
                border: '1px solid #444',
                overflow: 'hidden'
              }}
            >
              <button
                onClick={() => toggleStep(step.number)}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: expandedSteps[step.number] ? '#333' : '#2a2a2a',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                <span>Step {step.number}: {step.title}</span>
                {expandedSteps[step.number] ? <FaChevronUp /> : <FaChevronDown />}
              </button>

              {expandedSteps[step.number] && (
                <div style={{ padding: '12px', borderTop: '1px solid #444', backgroundColor: '#1a1a1a' }}>
                  <p style={{ color: '#ccc', marginBottom: '8px' }}>
                    {step.description}
                  </p>
                  {step.tips && step.tips.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <p style={{ fontSize: '12px', color: '#999', marginBottom: '4px', fontWeight: '500' }}>
                        💡 Tips:
                      </p>
                      <ul style={{ listStyle: 'none', padding: '0 0 0 16px', margin: 0 }}>
                        {step.tips.map((tip, idx) => (
                          <li key={idx} style={{ color: '#aaa', fontSize: '13px', marginBottom: '4px' }}>
                            • {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Warnings */}
        {currentGuide.warnings && currentGuide.warnings.length > 0 && (
          <div style={{ padding: '12px', backgroundColor: '#5d4037', borderRadius: '4px', border: '1px solid #8b4513' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#ffb74d' }}>
              <FaExclamationTriangle /> Important Safety Information
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {currentGuide.warnings.map((warning, idx) => (
                <li key={idx} style={{ padding: '4px 0', color: '#fff', fontSize: '13px' }}>
                  • {warning}
                </li>
              ))}
            </ul>
          </div>
        )}
        </div>

        {/* YOGi Assistant (GroqDrawerTrigger) - Outside main container for fixed positioning */}
        <GroqDrawerTrigger
          onOpen={(fn) => (groqDrawerOpenRef.current = fn)}
          setQuery={(fn) => (groqSetQueryRef.current = fn)}
          openDrawer={drawerOpen}
        />
      </>
    );
  }

  // Guide List View
  return (
    <>
      <div style={{ width: '100%', height: '100%', overflow: 'auto', backgroundColor: '#1a1a1a', color: '#fff', padding: '16px', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
            How To Guides
          </h1>
          <p style={{ color: '#ccc' }}>
            Learn essential outdoor skills for any adventure. Select a guide to get started.
          </p>
        </div>

        {/* Guides Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', flex: 1 }}>
          {GUIDES.map((guide) => (
            <button
              key={guide.id}
              onClick={() => setSelectedGuide(guide.id)}
              style={{
                padding: '16px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '4px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.3s',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#333';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px', color: '#fff' }}>
                {guide.title}
              </h3>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <span style={{ padding: '2px 8px', backgroundColor: '#1a1a1a', borderRadius: '3px', fontSize: '11px', color: '#999' }}>
                  {guide.category}
                </span>
                <span style={{ padding: '2px 8px', backgroundColor: '#1a1a1a', borderRadius: '3px', fontSize: '11px', color: getDifficultyColor(guide.difficulty) }}>
                  {guide.difficulty}
                </span>
              </div>
              <p style={{ color: '#aaa', fontSize: '13px', flex: 1, marginBottom: '12px' }}>
                {guide.overview}
              </p>
              <p style={{ color: '#666', fontSize: '12px' }}>
                ⏱ {guide.readTime} read
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* YOGi Assistant (GroqDrawerTrigger) - Outside main container for fixed positioning */}
      <GroqDrawerTrigger
        onOpen={(fn) => (groqDrawerOpenRef.current = fn)}
        setQuery={(fn) => (groqSetQueryRef.current = fn)}
        openDrawer={drawerOpen}
      />
    </>
  );
};

export default HowToPage;
